<template>
  <div class="indiefy-promotion-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="hero-content">
          <div class="hero-text">
            <h1 class="hero-title">
              {{ $t('promotion.heroTitle') }}
            </h1>
            <p class="hero-subtitle">
              {{ $t('promotion.heroSubtitle') }}
            </p>
            <div class="hero-actions">
              <button
                class="btn btn-primary btn-large"
                @click="redirectToPromo"
              >
                <i class="fas fa-rocket" />
                {{ $t('promotion.getStarted') }}
              </button>
              <button
                class="btn btn-outline btn-large"
                @click="openVideoModal"
              >
                <i class="fas fa-play" />
                {{ $t('promotion.watchDemo') }}
              </button>
            </div>
            <div class="hero-stats">
              <div class="stat">
                <span class="stat-number">10K+</span>
                <span class="stat-label">{{ $t('promotion.activePlaylists') }}</span>
              </div>
              <div class="stat">
                <span class="stat-number">$10</span>
                <span class="stat-label">{{ $t('promotion.startingPrice') }}</span>
              </div>
              <div class="stat">
                <span class="stat-number">24h</span>
                <span class="stat-label">{{ $t('promotion.activation') }}</span>
              </div>
            </div>
          </div>
          <div class="hero-media">
            <div
              class="video-placeholder"
              @click="openVideoModal"
            >
              <div class="video-overlay">
                <i class="fas fa-play-circle" />
                <span>{{ $t('promotion.watchVideo') }}</span>
              </div>
              <img
                src="/assets/images/indeify-promotion-video.jpg"
                alt="Indiefy Promotion"
                class="hero-image"
              >
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Video Modal -->
    <div
      v-if="showVideoModal"
      class="modal is-active"
    >
      <div
        class="modal-background"
        @click="closeVideoModal"
      />
      <div class="video-modal-container">
        <button
          class="modal-close-button video-close"
          aria-label="close"
          @click="closeVideoModal"
        >
          <i class="fas fa-times" />
        </button>
        <div class="video-wrapper">
          <iframe
            :src="videoEmbedUrl"
            title="YouTube video player"
            frameborder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowfullscreen
          />
        </div>
      </div>
    </div>

    <!-- Features Section -->
    <section class="features-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">
            {{ $t('promotion.featuresTitle') }}
          </h2>
          <p class="section-subtitle">
            {{ $t('promotion.featuresSubtitle') }}
          </p>
        </div>

        <!-- Playlisting Feature -->
        <div class="feature-block">
          <div class="feature-content">
            <div class="feature-text">
              <div class="feature-badge">
                <i class="fas fa-music" />
                {{ $t('promotion.playlisting') }}
              </div>
              <h3 class="feature-title">
                {{ $t('promotion.playlistingTitle') }}
              </h3>
              <p class="feature-description">
                {{ $t('promotion.playlistingDescription') }}
              </p>

              <div class="feature-benefits">
                <div class="benefit">
                  <i class="fas fa-check-circle" />
                  <span>{{ $t('promotion.workSmarter') }}</span>
                </div>
                <div class="benefit">
                  <i class="fas fa-check-circle" />
                  <span>{{ $t('promotion.workSmarterDesc') }}</span>
                </div>
                <div class="benefit">
                  <i class="fas fa-check-circle" />
                  <span>{{ $t('promotion.beDiscovered') }}</span>
                </div>
                <div class="benefit">
                  <i class="fas fa-check-circle" />
                  <span>{{ $t('promotion.beDiscoveredDesc') }}</span>
                </div>
              </div>

              <div class="feature-pricing">
                <span class="price">{{ $t('promotion.startingAt') }} <strong>$10/{{ $t('promotion.day') }}</strong></span>
              </div>
            </div>
            <div class="feature-visual">
              <div class="playlisting-demo">
                <div class="playlist-cards">
                  <div
                    v-for="i in 3"
                    :key="i"
                    class="playlist-card"
                  >
                    <div class="playlist-image" />
                    <div class="playlist-info">
                      <div class="playlist-name" />
                      <div class="playlist-followers" />
                    </div>
                  </div>
                </div>
                <div class="discovery-badge">
                  <i class="fas fa-star" />
                  {{ $t('promotion.discoverEcosystem') }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Digital Ads Feature -->
        <div class="feature-block reverse">
          <div class="feature-content">
            <div class="feature-text">
              <div class="feature-badge">
                <i class="fas fa-bullhorn" />
                {{ $t('promotion.digitalAds') }}
              </div>
              <h3 class="feature-title">
                {{ $t('promotion.digitalAdsTitle') }}
              </h3>
              <p class="feature-description">
                {{ $t('promotion.digitalAdsDescription') }}
              </p>

              <div class="feature-benefits">
                <div class="benefit">
                  <i class="fas fa-check-circle" />
                  <span>{{ $t('promotion.zeroToAd') }}</span>
                </div>
                <div class="benefit">
                  <i class="fas fa-check-circle" />
                  <span>{{ $t('promotion.zeroToAdDesc') }}</span>
                </div>
                <div class="benefit">
                  <i class="fas fa-check-circle" />
                  <span>{{ $t('promotion.noCreatives') }}</span>
                </div>
                <div class="benefit">
                  <i class="fas fa-check-circle" />
                  <span>{{ $t('promotion.noCreativesDesc') }}</span>
                </div>
              </div>

              <div class="feature-pricing">
                <span class="price">{{ $t('promotion.startingAt') }} <strong>$10/{{ $t('promotion.day') }}</strong></span>
              </div>
            </div>
            <div class="feature-visual">
              <div class="ads-demo">
                <div class="social-platforms">
                  <div class="platform facebook">
                    <i class="fab fa-facebook" />
                  </div>
                  <div class="platform instagram">
                    <i class="fab fa-instagram" />
                  </div>
                  <div class="platform tiktok">
                    <i class="fab fa-tiktok" />
                  </div>
                  <div class="platform more">
                    <span>+</span>
                  </div>
                </div>
                <div class="campaign-preview">
                  <div class="ad-mockup">
                    <div class="ad-image" />
                    <div class="ad-content">
                      <div class="ad-title" />
                      <div class="ad-description" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="how-it-works-section">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">
            {{ $t('promotion.howItWorksTitle') }}
          </h2>
          <p class="section-subtitle">
            {{ $t('promotion.howItWorksSubtitle') }}
          </p>
        </div>

        <div class="steps-container">
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="step"
          >
            <div class="step-number">
              {{ index + 1 }}
            </div>
            <div class="step-content">
              <h4 class="step-title">
                {{ $t(step.title) }}
              </h4>
              <p class="step-description">
                {{ $t(step.description) }}
              </p>
            </div>
            <div class="step-icon">
              <i :class="step.icon" />
            </div>
          </div>
        </div>

        <div class="results-preview">
          <h3 class="results-title">
            {{ $t('promotion.resultsTitle') }}
          </h3>
          <div class="results-grid">
            <div class="result-item">
              <i class="fas fa-heart" />
              <span>{{ $t('promotion.reviews') }}</span>
            </div>
            <div class="result-item">
              <i class="fas fa-headphones" />
              <span>{{ $t('promotion.listens') }}</span>
            </div>
            <div class="result-item">
              <i class="fas fa-list" />
              <span>{{ $t('promotion.placements') }}</span>
            </div>
            <div class="result-item">
              <i class="fas fa-chart-line" />
              <span>{{ $t('promotion.analytics') }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <div class="cta-content">
          <h2 class="cta-title">
            {{ $t('promotion.ctaTitle') }}
          </h2>
          <p class="cta-subtitle">
            {{ $t('promotion.ctaSubtitle') }}
          </p>
          <div class="cta-actions">
            <button
              class="btn btn-primary btn-xl"
              @click="redirectToPromo"
            >
              <i class="fas fa-rocket" />
              {{ $t('promotion.startPromotion') }}
            </button>
            <button
              class="btn btn-outline btn-xl"
              @click="redirectToPromo"
            >
              <i class="fas fa-info-circle" />
              {{ $t('promotion.learnMore') }}
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePageSeo } from '@/composables/usePageSeo';

export default {
  name: 'IndiefyPromotionView',
  setup() {
    const { t } = useI18n();

    usePageSeo({
      titleKey: 'promotion.seoTitle',
      descriptionKey: 'promotion.seoDescription',
    });

    const steps = ref([
      {
        title: 'promotion.step1Title',
        description: 'promotion.step1Description',
        icon: 'fas fa-shopping-cart',
      },
      {
        title: 'promotion.step2Title',
        description: 'promotion.step2Description',
        icon: 'fas fa-music',
      },
      {
        title: 'promotion.step3Title',
        description: 'promotion.step3Description',
        icon: 'fas fa-rocket',
      },
      {
        title: 'promotion.step4Title',
        description: 'promotion.step4Description',
        icon: 'fas fa-chart-line',
      },
    ]);

    // Function to redirect to promo website
    const redirectToPromo = () => {
      location.href = 'https://promo.indiefy.net/';
    };

    // Video Modal Logic
    const showVideoModal = ref(false);
    const youtubeVideoId = 'GFRU9rwu3ZI';
    const videoEmbedUrl = computed(() => `https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&rel=0`);

    const openVideoModal = () => {
      showVideoModal.value = true;
    };

    const closeVideoModal = () => {
      showVideoModal.value = false;
    };

    return {
      steps,
      redirectToPromo,
      showVideoModal,
      videoEmbedUrl,
      openVideoModal,
      closeVideoModal,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/breakpoints";

.indiefy-promotion-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;

    @media (max-width: $mobile) {
      padding: 0 0.75rem;
    }
  }
}

// Hero Section
.hero-section {
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;

  @media (max-width: $mobile) {
    min-height: auto;
    padding: 4rem 0;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%);
    z-index: 1;
  }

  .container {
    position: relative;
    z-index: 2;
    padding: 0 1rem;

    @media (max-width: $mobile) {
      padding: 0 0.75rem;
    }
  }

  .hero-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;

    @media (max-width: $tablet) {
      grid-template-columns: 1fr;
      gap: 3rem;
      text-align: center;
    }

    @media (max-width: $mobile) {
      gap: 2rem;
      padding: 1rem 0;
    }
  }

  .hero-text {
    color: white;

    .hero-title {
      font-size: 3.5rem;
      font-weight: 800;
      line-height: 1.1;
      margin-bottom: 1.5rem;

      @media (max-width: $tablet) {
        font-size: 2.5rem;
        margin-bottom: 1rem;
      }

      @media (max-width: $mobile) {
        font-size: 1.8rem;
        line-height: 1.2;
        margin-bottom: 0.75rem;
      }
    }

    .hero-subtitle {
      font-size: 1.25rem;
      line-height: 1.6;
      margin-bottom: 2.5rem;
      opacity: 0.9;

      @media (max-width: $tablet) {
        margin-bottom: 2rem;
      }

      @media (max-width: $mobile) {
        font-size: 1rem;
        line-height: 1.5;
        margin-bottom: 1.5rem;
      }
    }

    .hero-actions {
      display: flex;
      gap: 1rem;
      margin-bottom: 3rem;

      @media (max-width: $tablet) {
        margin-bottom: 2rem;
      }

      @media (max-width: $mobile) {
        flex-direction: column;
        gap: 0.75rem;
        margin-bottom: 1.5rem;
        width: 100%;
      }
    }

    .hero-stats {
      display: flex;
      gap: 2rem;

      @media (max-width: $tablet) {
        justify-content: center;
        gap: 1.5rem;
      }

      @media (max-width: $mobile) {
        flex-wrap: wrap;
        justify-content: center;
        gap: 1rem;
      }

      .stat {
        text-align: center;

        @media (max-width: $mobile) {
          min-width: 80px;
        }

        .stat-number {
          display: block;
          font-size: 2rem;
          font-weight: 700;
          color: #ffd700;

          @media (max-width: $mobile) {
            font-size: 1.6rem;
          }
        }

        .stat-label {
          font-size: 0.9rem;
          opacity: 0.8;

          @media (max-width: $mobile) {
            font-size: 0.8rem;
          }
        }
      }
    }
  }

  .hero-media {
    .video-placeholder {
      position: relative;
      border-radius: 20px;
      overflow: hidden;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
      cursor: pointer;
      transition: transform 0.3s ease;

      @media (max-width: $tablet) {
        border-radius: 15px;
        box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
      }

      @media (max-width: $mobile) {
        border-radius: 12px;
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
        max-width: 100%;
        margin: 0 auto;
      }

      &:hover {
        transform: scale(1.02);

        @media (max-width: $mobile) {
          transform: none;
        }
      }

      .hero-image {
        width: 100%;
        height: 400px;
        object-fit: cover;
        background: linear-gradient(45deg, #667eea, #764ba2);

        @media (max-width: $tablet) {
          height: 300px;
        }

        @media (max-width: $mobile) {
          height: 200px;
        }
      }

      .video-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.4);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: white;
        transition: background 0.3s ease;

        &:hover {
          background: rgba(0, 0, 0, 0.6);
        }

        i {
          font-size: 4rem;
          margin-bottom: 1rem;
          color: #ffd700;

          @media (max-width: $mobile) {
            font-size: 2.5rem;
            margin-bottom: 0.5rem;
          }
        }

        span {
          font-size: 1.1rem;
          font-weight: 600;

          @media (max-width: $mobile) {
            font-size: 0.9rem;
          }
        }
      }
    }
  }
}

// Features Section
.features-section {
  padding: 8rem 0;
  background: white;

  @media (max-width: $tablet) {
    padding: 6rem 0;
  }

  @media (max-width: $mobile) {
    padding: 4rem 0;
  }

  .container {
    padding: 0 1rem;

    @media (max-width: $mobile) {
      padding: 0 0.75rem;
    }
  }

  .section-header {
    text-align: center;
    margin-bottom: 6rem;

    @media (max-width: $tablet) {
      margin-bottom: 4rem;
    }

    @media (max-width: $mobile) {
      margin-bottom: 3rem;
    }

    .section-title {
      font-size: 2.5rem;
      font-weight: 700;
      color: #2d3748;
      margin-bottom: 1rem;

      @media (max-width: $tablet) {
        font-size: 2rem;
      }

      @media (max-width: $mobile) {
        font-size: 1.6rem;
        margin-bottom: 0.75rem;
      }
    }

    .section-subtitle {
      font-size: 1.2rem;
      color: #718096;
      max-width: 600px;
      margin: 0 auto;

      @media (max-width: $mobile) {
        font-size: 1rem;
        max-width: 100%;
        padding: 0 1rem;
      }
    }
  }

  .feature-block {
    margin-bottom: 8rem;

    @media (max-width: $tablet) {
      margin-bottom: 6rem;
    }

    @media (max-width: $mobile) {
      margin-bottom: 4rem;
    }

    &:last-child {
      margin-bottom: 0;
    }

    .feature-content {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 6rem;
      align-items: center;

      @media (max-width: $tablet) {
        grid-template-columns: 1fr;
        gap: 4rem;
      }

      @media (max-width: $mobile) {
        gap: 2rem;
      }
    }

    &.reverse .feature-content {
      .feature-text {
        order: 2;
      }

      .feature-visual {
        order: 1;
      }

      @media (max-width: $tablet) {
        .feature-text {
          order: 1;
        }

        .feature-visual {
          order: 2;
        }
      }
    }

    .feature-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      background: linear-gradient(135deg, #667eea, #764ba2);
      color: white;
      padding: 0.5rem 1rem;
      border-radius: 50px;
      font-size: 0.9rem;
      font-weight: 600;
      margin-bottom: 1.5rem;

      @media (max-width: $mobile) {
        font-size: 0.8rem;
        padding: 0.4rem 0.8rem;
        margin-bottom: 1rem;
        gap: 0.4rem;
      }
    }

    .feature-title {
      font-size: 2rem;
      font-weight: 700;
      color: #2d3748;
      margin-bottom: 1rem;

      @media (max-width: $tablet) {
        font-size: 1.7rem;
      }

      @media (max-width: $mobile) {
        font-size: 1.4rem;
        margin-bottom: 0.75rem;
      }
    }

    .feature-description {
      font-size: 1.1rem;
      color: #718096;
      line-height: 1.6;
      margin-bottom: 2rem;

      @media (max-width: $tablet) {
        margin-bottom: 1.5rem;
      }

      @media (max-width: $mobile) {
        font-size: 1rem;
        line-height: 1.5;
        margin-bottom: 1rem;
      }
    }

    .feature-benefits {
      margin-bottom: 2rem;

      @media (max-width: $tablet) {
        margin-bottom: 1.5rem;
      }

      @media (max-width: $mobile) {
        margin-bottom: 1rem;
      }

      .benefit {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        margin-bottom: 1rem;

        @media (max-width: $mobile) {
          gap: 0.75rem;
          margin-bottom: 0.75rem;
        }

        i {
          color: #48bb78;
          margin-top: 0.2rem;
          flex-shrink: 0;

          @media (max-width: $mobile) {
            font-size: 0.9rem;
            margin-top: 0.1rem;
          }
        }

        span {
          color: #4a5568;
          line-height: 1.5;

          @media (max-width: $mobile) {
            font-size: 0.9rem;
            line-height: 1.4;
          }
        }
      }
    }

    .feature-pricing {
      .price {
        font-size: 1.1rem;
        color: #718096;

        strong {
          color: #667eea;
          font-size: 1.3rem;
        }
      }
    }
  }

  // Feature Visuals
  .playlisting-demo {
    background: linear-gradient(135deg, #f7fafc, #edf2f7);
    border-radius: 20px;
    padding: 2rem;
    position: relative;

    .playlist-cards {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      .playlist-card {
        background: white;
        border-radius: 12px;
        padding: 1rem;
        display: flex;
        align-items: center;
        gap: 1rem;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

        .playlist-image {
          width: 60px;
          height: 60px;
          background: linear-gradient(45deg, #667eea, #764ba2);
          border-radius: 8px;
        }

        .playlist-info {
          flex: 1;

          .playlist-name {
            height: 16px;
            background: #e2e8f0;
            border-radius: 4px;
            margin-bottom: 0.5rem;
          }

          .playlist-followers {
            height: 12px;
            background: #f1f5f9;
            border-radius: 4px;
            width: 60%;
          }
        }
      }
    }

    .discovery-badge {
      position: absolute;
      top: -10px;
      right: -10px;
      background: #ffd700;
      color: #2d3748;
      padding: 0.5rem 1rem;
      border-radius: 50px;
      font-size: 0.9rem;
      font-weight: 600;
      box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);

      i {
        margin-right: 0.5rem;
      }
    }
  }

  .ads-demo {
    background: linear-gradient(135deg, #f7fafc, #edf2f7);
    border-radius: 20px;
    padding: 2rem;

    .social-platforms {
      display: flex;
      justify-content: center;
      gap: 1rem;
      margin-bottom: 2rem;

      .platform {
        width: 60px;
        height: 60px;
        border-radius: 15px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 1.5rem;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);

        &.facebook {
          background: #1877f2;
        }

        &.instagram {
          background: linear-gradient(45deg, #e4405f, #f77737);
        }

        &.tiktok {
          background: #000000;
        }

        &.more {
          background: #718096;
          font-size: 2rem;
          font-weight: 300;
        }
      }
    }

    .campaign-preview {
      .ad-mockup {
        background: white;
        border-radius: 12px;
        padding: 1.5rem;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

        .ad-image {
          height: 120px;
          background: linear-gradient(45deg, #667eea, #764ba2);
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        .ad-content {
          .ad-title {
            height: 16px;
            background: #e2e8f0;
            border-radius: 4px;
            margin-bottom: 0.5rem;
          }

          .ad-description {
            height: 12px;
            background: #f1f5f9;
            border-radius: 4px;
            width: 80%;
          }
        }
      }
    }
  }
}

// How It Works Section
.how-it-works-section {
  padding: 8rem 0;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);

  @media (max-width: $tablet) {
    padding: 6rem 0;
  }

  @media (max-width: $mobile) {
    padding: 4rem 0;
  }

  .container {
    padding: 0 1rem;

    @media (max-width: $mobile) {
      padding: 0 0.75rem;
    }
  }

  .section-header {
    text-align: center;
    margin-bottom: 6rem;

    @media (max-width: $tablet) {
      margin-bottom: 4rem;
    }

    @media (max-width: $mobile) {
      margin-bottom: 3rem;
    }

    .section-title {
      font-size: 2.5rem;
      font-weight: 700;
      color: #2d3748;
      margin-bottom: 1rem;

      @media (max-width: $tablet) {
        font-size: 2rem;
      }

      @media (max-width: $mobile) {
        font-size: 1.6rem;
        margin-bottom: 0.75rem;
      }
    }

    .section-subtitle {
      font-size: 1.2rem;
      color: #718096;
      max-width: 600px;
      margin: 0 auto;

      @media (max-width: $mobile) {
        font-size: 1rem;
        max-width: 100%;
        padding: 0 1rem;
      }
    }
  }

  .steps-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 3rem;
    margin-bottom: 6rem;

    @media (max-width: $tablet) {
      gap: 2rem;
      margin-bottom: 4rem;
    }

    @media (max-width: $mobile) {
      grid-template-columns: 1fr;
      gap: 1.5rem;
      margin-bottom: 3rem;
    }

    .step {
      background: white;
      border-radius: 20px;
      padding: 2.5rem 2rem;
      text-align: center;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
      transition: transform 0.3s ease;

      @media (max-width: $tablet) {
        padding: 2rem 1.5rem;
        border-radius: 15px;
      }

      @media (max-width: $mobile) {
        padding: 1.5rem 1rem;
        border-radius: 12px;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
      }

      &:hover {
        transform: translateY(-10px);

        @media (max-width: $mobile) {
          transform: translateY(-5px);
        }
      }

      .step-number {
        width: 60px;
        height: 60px;
        background: linear-gradient(135deg, #667eea, #764ba2);
        color: white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.5rem;
        font-weight: 700;
        margin: 0 auto 1.5rem;

        @media (max-width: $mobile) {
          width: 50px;
          height: 50px;
          font-size: 1.2rem;
          margin-bottom: 1rem;
        }
      }

      .step-title {
        font-size: 1.3rem;
        font-weight: 600;
        color: #2d3748;
        margin-bottom: 1rem;

        @media (max-width: $mobile) {
          font-size: 1.1rem;
          margin-bottom: 0.75rem;
        }
      }

      .step-description {
        color: #718096;
        line-height: 1.6;
        margin-bottom: 1.5rem;

        @media (max-width: $mobile) {
          font-size: 0.9rem;
          line-height: 1.5;
          margin-bottom: 1rem;
        }
      }

      .step-icon {
        i {
          font-size: 2rem;
          color: #667eea;

          @media (max-width: $mobile) {
            font-size: 1.5rem;
          }
        }
      }
    }
  }

  .results-preview {
    text-align: center;
    background: white;
    border-radius: 20px;
    padding: 3rem;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);

    @media (max-width: $tablet) {
      padding: 2rem;
      border-radius: 15px;
    }

    @media (max-width: $mobile) {
      padding: 1.5rem;
      border-radius: 12px;
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
    }

    .results-title {
      font-size: 1.8rem;
      font-weight: 600;
      color: #2d3748;
      margin-bottom: 2rem;

      @media (max-width: $tablet) {
        font-size: 1.5rem;
        margin-bottom: 1.5rem;
      }

      @media (max-width: $mobile) {
        font-size: 1.3rem;
        margin-bottom: 1rem;
      }
    }

    .results-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 2rem;

      @media (max-width: $tablet) {
        gap: 1.5rem;
      }

      @media (max-width: $mobile) {
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;
      }

      .result-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;

        i {
          font-size: 2.5rem;
          color: #667eea;
        }

        span {
          color: #4a5568;
          font-weight: 600;
        }
      }
    }
  }
}

// CTA Section
.cta-section {
  padding: 8rem 0;
  background: linear-gradient(135deg, #2d3748 0%, #4a5568 100%);
  color: white;
  text-align: center;

  @media (max-width: $tablet) {
    padding: 6rem 0;
  }

  @media (max-width: $mobile) {
    padding: 4rem 0;
  }

  .container {
    padding: 0 1rem;

    @media (max-width: $mobile) {
      padding: 0 0.75rem;
    }
  }

  .cta-title {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;

    @media (max-width: $tablet) {
      font-size: 2rem;
    }

    @media (max-width: $mobile) {
      font-size: 1.6rem;
      margin-bottom: 0.75rem;
    }
  }

  .cta-subtitle {
    font-size: 1.2rem;
    opacity: 0.9;
    margin-bottom: 3rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;

    @media (max-width: $tablet) {
      margin-bottom: 2.5rem;
    }

    @media (max-width: $mobile) {
      font-size: 1rem;
      margin-bottom: 2rem;
      max-width: 100%;
      padding: 0 1rem;
    }
  }

  .cta-actions {
    display: flex;
    justify-content: center;
    gap: 1.5rem;
    margin-bottom: 2rem;

    @media (max-width: $tablet) {
      gap: 1rem;
    }

    @media (max-width: $mobile) {
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
    }
  }

  .cta-note {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    opacity: 0.8;
    font-size: 0.9rem;

    @media (max-width: $mobile) {
      font-size: 0.85rem;
      gap: 0.4rem;
    }

    i {
      color: #48bb78;
    }
  }
}

// Button Styles
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  justify-content: center;

  @media (max-width: $mobile) {
    width: 100%;
    padding: 0.9rem 1.5rem;
    font-size: 0.95rem;
    gap: 0.4rem;
  }

  &.btn-large {
    padding: 1.2rem 2.5rem;
    font-size: 1.1rem;

    @media (max-width: $mobile) {
      padding: 1rem 2rem;
      font-size: 1rem;
    }
  }

  &.btn-xl {
    padding: 1.5rem 3rem;
    font-size: 1.2rem;

    @media (max-width: $mobile) {
      padding: 1.2rem 2rem;
      font-size: 1.1rem;
    }
  }

  &.btn-primary {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);

      @media (max-width: $mobile) {
        transform: translateY(-1px);
        box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
      }
    }
  }

  &.btn-outline {
    background: transparent;
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.5);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }
}

// Responsive video wrapper for YouTube iframe
.video-wrapper {
  position: relative;
  width: 100%;
  padding-top: 56.25%;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
}

// Video modal container
.video-modal-container {
  position: relative;
  width: 90%;
  max-width: 1200px;
  margin: 2rem auto;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.5);

  @media (max-width: $tablet) {
    width: 95%;
    margin: 1rem auto;
    max-height: calc(100vh - 2rem);
  }

  @media (max-width: $mobile) {
    width: 98%;
    margin: 0.5rem auto;
    max-height: calc(100vh - 1rem);
    border-radius: 4px;
  }

  .modal-close-button.video-close {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.75);
    color: #fff;
    cursor: pointer;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
    transition: all 0.3s ease;
    z-index: 10;
    font-size: 16px;

    @media (max-width: $mobile) {
      top: 8px;
      right: 8px;
      width: 36px;
      height: 36px;
      font-size: 14px;
    }

    &:hover {
      background: rgba(0, 0, 0, 0.9);
      transform: scale(1.1);
    }

    i {
      line-height: 1;
    }
  }
}
</style>
